import React, { Children } from 'react'
import PropTypes from 'prop-types'
import { lowerCase, capitalize } from 'lodash'

import { Typography, useTheme, Divider, darken } from '@mui/material'
import { orange } from '@mui/material/colors'

import { get } from 'utils/lodash'
import { formatCurrency, formatCentsToCurrency, roundToScale } from 'utils/price'
import { isValidHex } from 'utils/validations'

import T from 'T'

import PreviewField from './fields/PreviewField'
import DisposalFeeRateField from './fields/DisposalFeeRateField'
import MinimumChargeField from './fields/MinimumChargeField'
import IncludedField from './fields/IncludedField'
import OverageFeeField from './fields/OverageFeeField'
import OverageThresholdField from './fields/OverageThresholdField'

import { getAppliedRate, getOverageFee, getHasMinimumCharge } from './utils'
import { getFormattedUom } from '../utils'

const Preview = ({ index, isEditable = false, material }) => {
  const theme = useTheme()
  const {
    materialType,
    materialColor,
    unitOfMeasure,
    quantity,
    unitPriceDollars,
    useDisposalFeeRate,
    rate,
    useMinimumCharge,
    minimumCharge,
    includedTonnage,
    chargeableTonnage,
    overageWeight,
    overageFee,
    disposalFeeData,
  } = material
  const formattedColor = isValidHex(materialColor) ? darken(materialColor, 0.3) : theme.palette.text.primary
  const unit = lowerCase(unitOfMeasure)
  const appliedRate = getAppliedRate(useDisposalFeeRate, rate, unitPriceDollars)
  const hasMinimumCharge = getHasMinimumCharge(useMinimumCharge, minimumCharge)
  const calculatedOverageFee = getOverageFee(quantity, overageWeight, overageFee)
  const showIncludedLabel = includedTonnage > 0 || hasMinimumCharge
  const calculatedChargeableTonnage = Number(includedTonnage) ? roundToScale(chargeableTonnage) : chargeableTonnage

  return (
    <>
      <Typography fontWeight={500} color={formattedColor}>
        {materialType}
      </Typography>
      {!isEditable && (
        <PreviewField
          isEditable={isEditable}
          labelLeft={`${T.RATE}:`}
          labelRight={`${formatCurrency(appliedRate, { maximumFractionDigits: 4 })} / ${unit}`}
        />
      )}
      {isEditable && <DisposalFeeRateField index={index} unit={unit} />}
      <PreviewField isEditable={isEditable} labelLeft={`Total ${unit}s:`} labelRight={`${quantity} ${getFormattedUom(unit, quantity)}`} />
      {isEditable && <MinimumChargeField index={index} />}
      {!isEditable && showIncludedLabel && (
        <PreviewField
          labelLeft={
            <>
              {`Included ${unit}s`}
              {hasMinimumCharge && <>{` (min. charge = ${formatCurrency(minimumCharge)})`}</>}
              {':'}
            </>
          }
          labelRight={`${includedTonnage} ${getFormattedUom(unit, includedTonnage)}`}
          rightProps={{ whiteSpace: 'nowrap' }}
        />
      )}
      {isEditable && <IncludedField index={index} unit={unit} />}
      {chargeableTonnage > 0 && (
        <PreviewField
          isEditable={isEditable}
          labelLeft={`Chargeable ${unit}s:`}
          labelRight={`${calculatedChargeableTonnage} ${getFormattedUom(unit, chargeableTonnage)}`}
        />
      )}
      {isEditable && <OverageThresholdField index={index} unit={unit} />}
      {!isEditable && calculatedOverageFee > 0 && (
        <>
          <PreviewField
            labelLeft={`${capitalize(T.OVERAGE_FEE)}:`}
            labelRight={formatCurrency(calculatedOverageFee)}
            leftProps={{ color: orange[600] }}
            rightProps={{ color: orange[600] }}
          />
          <Typography>{`(Applied when ${unit}s exceeds ${overageWeight})`}</Typography>
        </>
      )}
      {isEditable && <OverageFeeField index={index} />}
      <PreviewField isEditable={isEditable} labelLeft="Subtotal:" labelRight={formatCentsToCurrency(disposalFeeData.subtotalCents)} />
      {Children.toArray(
        get(disposalFeeData, 'taxes', []).map(tax => {
          const taxName = get(tax, 'taxName', '')
          const formattedTaxPrice = get(tax, 'formattedTaxPrice', '')
          const taxAmountCents = get(tax, 'taxAmountCents', 0)

          return (
            <PreviewField
              isEditable={isEditable}
              labelLeft={`${taxName} - ${formattedTaxPrice}:`}
              labelRight={formatCentsToCurrency(taxAmountCents)}
            />
          )
        })
      )}
      <Divider sx={{ my: 1 }} />
    </>
  )
}

Preview.propTypes = {
  index: PropTypes.number,
  isEditable: PropTypes.bool,
  material: PropTypes.object,
}

export default Preview
